import React, { useEffect, useState, useRef } from 'react';
import { MapPinIcon, LoaderIcon, XIcon, GlobeIcon } from 'lucide-react';
import { NormalizedLocation, geocodeQuery, isGeocodingAvailable, buildManualLocation } from '../utils/locationUtils';
interface LocationAutocompleteProps {
  value: NormalizedLocation | null;
  onChange: (location: NormalizedLocation | null) => void;
  disabled?: boolean;
  darkMode?: boolean;
  placeholder?: string;
  /** Legacy free-text fallback (e.g. "Los Angeles, CA") shown when no normalized
   *  location is set yet. Helps existing teachers see their old data. */
  legacyDisplay?: string;
  /** Called when the user clears the field. */
  onClear?: () => void;
}
/**
 * Location autocomplete with a single canonical output: a NormalizedLocation.
 *
 * Behavior:
 * - When a Mapbox token is configured, fetches city/region/country suggestions
 *   as the user types (debounced 250ms) and emits a fully normalized location
 *   (with lat/lng/geohash) on selection.
 * - When no token is configured, gracefully degrades to a plain text input
 *   that emits a 'manual' NormalizedLocation on blur (no coordinates — the
 *   teacher can later re-enter their location to enable proximity features).
 */
export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  darkMode = false,
  placeholder = 'Start typing your city…',
  legacyDisplay = '',
  onClear
}) => {
  const geocodingAvailable = isGeocodingAvailable();
  // Display string in the input
  const [query, setQuery] = useState<string>(value?.formatted || legacyDisplay || '');
  const [suggestions, setSuggestions] = useState<NormalizedLocation[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);
  // Sync external value changes into the input
  useEffect(() => {
    setQuery(value?.formatted || legacyDisplay || '');
  }, [value?.formatted, legacyDisplay]);
  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  // Debounced geocoding
  useEffect(() => {
    if (!geocodingAvailable || disabled) return;
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    if (!query.trim() || query === value?.formatted) {
      setSuggestions([]);
      setIsFetching(false);
      return;
    }
    setIsFetching(true);
    debounceRef.current = window.setTimeout(async () => {
      // Cancel previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const results = await geocodeQuery(query, {
        limit: 5,
        signal: controller.signal
      });
      if (!controller.signal.aborted) {
        setSuggestions(results);
        setIsFetching(false);
        setIsOpen(true);
        setActiveIdx(-1);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, geocodingAvailable, disabled, value?.formatted]);
  const handleSelect = (loc: NormalizedLocation) => {
    onChange(loc);
    setQuery(loc.formatted);
    setSuggestions([]);
    setIsOpen(false);
    setActiveIdx(-1);
  };
  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    onChange(null);
    onClear?.();
  };
  const handleManualBlur = () => {
    // Fallback path: no Mapbox token. Convert raw text to a manual location.
    if (geocodingAvailable) return;
    const trimmed = query.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }
    if (trimmed === value?.formatted) return;
    // Try to split "City, State" — fall back to whole string as city
    const parts = trimmed.split(',').map(p => p.trim());
    const manual = buildManualLocation(parts[0] || trimmed, parts[1] || '');
    if (manual) onChange(manual);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };
  const inputClasses = `w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`;
  return <div ref={containerRef} className="relative" data-id="element-1829">
      <div className="relative" data-id="element-1830">
        <MapPinIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1831" />
        <input type="text" value={query} onChange={e => {
        setQuery(e.target.value);
        if (geocodingAvailable) setIsOpen(true);
      }} onFocus={() => {
        if (geocodingAvailable && suggestions.length > 0) setIsOpen(true);
      }} onBlur={handleManualBlur} onKeyDown={handleKeyDown} disabled={disabled} placeholder={placeholder} className={inputClasses} autoComplete="off" aria-autocomplete="list" aria-expanded={isOpen} data-id="element-1832" />
        {/* Right-side adornment: spinner, clear, or status icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center" data-id="element-1833">
          {isFetching ? <LoaderIcon className={`h-4 w-4 animate-spin ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1834" /> : query && !disabled ? <button type="button" onClick={handleClear} className={`p-0.5 rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} aria-label="Clear location" data-id="element-1835">
              <XIcon className="h-4 w-4" data-id="element-1836" />
            </button> : null}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && !disabled && <ul className={`absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-lg shadow-lg border ${darkMode ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-200'}`} role="listbox" data-id="element-1837">
          {suggestions.map((s, idx) => <li key={`${s.formatted}-${idx}`} role="option" aria-selected={idx === activeIdx} onMouseDown={e => {
        e.preventDefault(); // prevent input blur before click
        handleSelect(s);
      }} onMouseEnter={() => setActiveIdx(idx)} className={`px-4 py-2.5 cursor-pointer flex items-start gap-2 transition-colors ${idx === activeIdx ? darkMode ? 'bg-navy-700' : 'bg-blue-50' : ''} ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-blue-50'}`} data-id="element-1838">
              <MapPinIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1839" />
              <div className="min-w-0 flex-1" data-id="element-1840">
                <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-1841">
                  {s.city || s.stateFull || s.formatted.split(',')[0]}
                </p>
                <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1842">
                  {s.formatted}
                </p>
              </div>
            </li>)}
        </ul>}

      {/* Helper text */}
      {!geocodingAvailable && <p className={`mt-1.5 text-xs flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-1843">
          <GlobeIcon className="h-3 w-3" data-id="element-1844" />
          Location autocomplete is currently unavailable. Enter your city
          manually.
        </p>}
    </div>;
};